import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { alertError, alertSuccess } from '../../components/Toast';
import { setData } from '../../core/redux/app/Actions';
import useVehicleForm from '../../core/hooks/useVehicleForm';
import { saveVehicle, getExistedPlate } from '../../core/utils/vehicles';
import ModalVehicles from '../../screens/ModalVehicles/ModalVehicles';
import { supabase } from '../../supabaseClient';
import { compareValues } from '../ModalClients/ModalClientsContainer';

const ModalVehiclesContainer = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const id = location.pathname.split('/')[3];
  const isNew = params.id === '0';
  const user = useSelector((state) => state.app.user || {});
  const reloadVehicles = useSelector(
    (state) => state.app.reloadVehicles || false
  );
  const company = useSelector((state) => state.app.company || {});

  const [oldValues, setOldValues] = useState({});
  const [responsible, setResponsible] = useState([]);

  const {
    values,
    setValues,
    loadingPlate,
    brandOptions,
    modelOptions,
    onChange: hookOnChange,
    createOption: hookCreateOption,
    changePlate: hookChangePlate,
    loadBrands,
  } = useVehicleForm();

  const onChange = (field, value) => hookOnChange({ field, value });
  const createOption = (name, field) => hookCreateOption({ name, field });
  const changePlate = (oldPlate) =>
    hookChangePlate({ plate: oldPlate, skipIfSame: oldValues?.plate });

  const loadData = async () => {
    let { data, error } = await supabase
      .from('Vehicle')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null);

    if (!error) {
      const item = data[0];
      setValues(item);
      setOldValues(item);
    }
  };

  useEffect(() => {
    if (id !== '0') {
      loadData();
    }
  }, [id]);

  useEffect(() => {
    const onBackButtonEvent = (e) => {
      e.preventDefault();
      closeModal();
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, []);

  const closeModal = () => {
    if (compareValues(oldValues, values)) {
      Swal.fire({
        title: 'Deseja sair sem salvar?',
        showCancelButton: true,
        showConfirmButton: false,
        showDenyButton: true,
        icon: 'warning',
        text: 'Ao sair, seus dados serão descartados.',
        denyButtonText: `Sair`,
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isDenied) {
          history.push('/vehicles');
        }
      });
    } else {
      history.push('/vehicles');
    }
  };

  useEffect(() => {
    loadResponsible();
    loadBrands();
  }, [company.id]);

  const loadResponsible = async () => {
    let allData = [];
    const pageSize = 1000;
    let page = 0;
    let hasMore = true;

    while (hasMore) {
      const from = page * pageSize;
      const to = from + pageSize - 1;

      let { data: pageData, error } = await supabase
        .from('Client')
        .select('*')
        .eq('id_company', company.id)
        .is('deleted_at', null)
        .range(from, to)
        .order('name', { ascending: true });

      if (error) break;

      if (pageData && pageData.length > 0) {
        allData = [...allData, ...pageData];
      }

      hasMore = pageData && pageData.length === pageSize;
      page++;
    }

    setResponsible(
      allData.map((item) => ({ label: item.name, value: item.id }))
    );
  };

  const onSave = async () => {
    const existedPlate = await getExistedPlate({
      plate: values.plate,
      companyId: company.id,
      excludeVehicleId: values.id,
    });

    try {
      const extraData = {};

      if (values && !values.brand) {
        alertError('Marca do veículo é obrigatória.');
        return;
      } else {
        if (values?.brand?.label) {
          extraData['brand'] = values?.brand?.label;
        }
      }

      if (values && !values.model) {
        alertError('Modelo do veículo é obrigatório.');
        return;
      } else {
        if (values?.model?.label) {
          extraData['model'] = values?.model?.label;
        }
      }

      if (values && !values.plate) {
        alertError('Placa do veículo é obrigatória.');
        return;
      }

      if (existedPlate?.length > 0) {
        alertError('Placa já cadastrada.');
        return;
      }

      await saveVehicle(values.id, {
        company: company.id,
        ...values,
        ...extraData,
      });
      alertSuccess('Sucesso ao salvar o veículo!');
      history.push('/vehicles');
      dispatch(setData({ reloadVehicles: !reloadVehicles }));
    } catch (error) {
      alertError('Erro ao salvar o veículo, tente novamente mais tarde.');
    }
  };

  const onRemove = async () => {
    const confirmation = await Swal.fire({
      title: 'Deseja mesmo excluir?',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      icon: 'warning',
      text: 'Seus dados serão completamente perdidos!',
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });

    if (confirmation.isDenied) {
      if (values && values.id) {
        const { data, error } = await supabase
          .from('Vehicle')
          .update({ deleted_at: new Date(), deleted_by: user.id })
          .eq('id', values.id);

        if (!error) {
          alertSuccess('Veículo removido com sucesso!');
          history.push('/vehicles');
          dispatch(setData({ reloadVehicles: !reloadVehicles }));
        } else {
          alertError('Erro ao remover o veículo, tente novamente mais tarde');
        }
      } else {
        alertError('Erro ao remover o veículo, tente novamente mais tarde');
      }
    } else {
      history.push('/vehicles');
    }
  };

  return (
    <ModalVehicles
      compareValues={compareValues(oldValues, values)}
      responsible={responsible}
      closeModal={closeModal}
      onRemove={onRemove}
      onChange={onChange}
      onSave={onSave}
      values={values}
      isNew={isNew}
      changePlate={changePlate}
      loadingPlate={loadingPlate}
      brandOptions={brandOptions}
      modelOptions={modelOptions}
      createOption={createOption}
    />
  );
};

export default ModalVehiclesContainer;
